// Waiting-room display: a screen beside the kiosk showing which visit codes are with the doctor and
// who is waiting. Patients are shown only by the visit code on their printed slip.

import { useEffect, useState } from 'react';
import { kioskApi, kioskCreds, type KioskCredentials } from '../lib/api.ts';
import { GovHeader, NoticeBar, PROGRAM, useClock } from '../ui/site.tsx';

interface Board {
  facility: { name: string; name_ur: string; type: string; district: string; district_ur: string } | null;
  serverTime: string;
  withDoctor: { token: string; mode: string; since: string; clinician: string | null }[];
  waiting: { token: string; mode: string; since: string }[];
  recent: { token: string; at: string }[];
  stats: { screened: number; avgMinutes: number | null; teleconsults: number };
}

const MODE: Record<string, string> = { video: 'Video consult', audio: 'Audio consult', chat: 'Chat consult', async: 'Doctor review' };
const MODE_UR: Record<string, string> = { video: 'ویڈیو مشورہ', audio: 'آڈیو مشورہ', chat: 'چیٹ مشورہ', async: 'ڈاکٹر کا جائزہ' };

const NOTICES = [
  'Please keep your printed slip — your visit code is called on this screen',
  'اپنی پرچی سنبھال کر رکھیں — آپ کا وزٹ کوڈ اسی اسکرین پر دکھایا جائے گا',
  'Urgent readings are seen by staff first — if you feel unwell, tell the duty staff now',
  'High blood pressure often has no symptoms — check it every year after 40',
  'بلڈ پریشر کی جانچ ہر سال کروائیں — صحت مند زندگی، محفوظ مستقبل',
  'All health services at this facility are provided free of charge by the Government of AJK',
];

// In the pilot each display is paired with its facility's kiosk. In the demonstration the screen
// uses the provisioned kiosk on this browser, or a demo kiosk chosen with ?k=001…025.
function credentials(): KioskCredentials {
  const k = new URLSearchParams(location.search).get('k');
  if (k && /^\d{3}$/.test(k)) return { kioskId: `AJK-K-${k}`, key: `demo-kiosk-key-${k}` };
  return kioskCreds.load() ?? { kioskId: 'AJK-K-001', key: 'demo-kiosk-key-001' };
}

const minutesSince = (iso: string, now: Date) => Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60000));

export function DisplayPage() {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState(false);
  const now = useClock(15_000);

  useEffect(() => {
    const api = kioskApi(credentials());
    let stop = false;
    const load = () => api.get<Board>('/api/kiosk/display').then((b) => { if (!stop) { setBoard(b); setError(false); } }).catch(() => !stop && setError(true));
    load();
    const id = setInterval(load, 10_000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const f = board?.facility;
  const slots = Math.max(4, board?.withDoctor.length ?? 0);
  const cards = Array.from({ length: slots }, (_, i) => board?.withDoctor[i] ?? null);

  return (
    <div className="display">
      <GovHeader
        title={f ? f.name : PROGRAM.en}
        subtitle={f ? `${f.district} · ${PROGRAM.en}, ${PROGRAM.sub}` : PROGRAM.sub}
        urdu={f ? f.name_ur : PROGRAM.ur}
      />
      <NoticeBar items={NOTICES} badge="NOTICE" aside={error ? 'Reconnecting…' : 'Live'} />

      <div className="display-main">
        <section>
          <div className="display-h"><span className="dot" />With the doctor <span lang="ur" style={{ fontFamily: 'var(--font-ur)', letterSpacing: 0, textTransform: 'none' }}>ڈاکٹر کے پاس</span></div>
          <div className="display-cards">
            {cards.map((c, i) => c ? (
              <div key={c.token} className="dcard">
                <div className="dcard-head"><span>Teleconsult room {i + 1}</span><span className="dcard-state">In consultation</span></div>
                <div className="dcard-body">
                  <div>
                    <div className="dcard-token">{c.token}</div>
                    <div className="dcard-sub">{MODE[c.mode] ?? c.mode} · <span lang="ur" style={{ fontFamily: 'var(--font-ur)' }}>{MODE_UR[c.mode]}</span></div>
                  </div>
                </div>
                <div className="dcard-foot">{c.clinician ?? 'Department doctor'} · {minutesSince(c.since, now)} min</div>
              </div>
            ) : (
              <div key={`empty-${i}`} className="dcard empty">
                <div className="dcard-head"><span>Teleconsult room {i + 1}</span><span className="dcard-state wait">Available</span></div>
                <div className="dcard-body"><div><div className="dcard-token" style={{ opacity: 0.35 }}>— — —</div><div className="dcard-sub">Next patient will be called here</div></div></div>
                <div className="dcard-foot">Department of Health · AJK</div>
              </div>
            ))}
          </div>
          {board && board.recent.length > 0 && (
            <>
              <div className="display-h" style={{ marginTop: 22 }}>Screening complete — collect your slip</div>
              <div className="display-chips">{board.recent.map((r) => <span key={r.token} className="display-chip">{r.token}</span>)}</div>
            </>
          )}
        </section>

        <section>
          <div className="display-h">Waiting <span className="count">{board?.waiting.length ?? 0}</span></div>
          <div className="dlist">
            <table>
              <thead><tr><th>#</th><th>Visit code</th><th>Service</th><th style={{ textAlign: 'end' }}>Waiting</th></tr></thead>
              <tbody>
                {board?.waiting.length ? board.waiting.slice(0, 9).map((w, i) => {
                  const m = minutesSince(w.since, now);
                  return (
                    <tr key={w.token} className={i === 0 ? 'first' : ''}>
                      <td>{i + 1}</td>
                      <td className="tok">{w.token}</td>
                      <td>{MODE[w.mode] ?? w.mode}</td>
                      <td className={`wait ${m > 30 ? 'long' : ''}`}>{m} min</td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#8aa293', padding: '36px 16px' }}>{board ? 'No patients waiting for a teleconsult' : 'Connecting…'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p style={{ color: '#8aa293', fontSize: '0.85rem', marginTop: 12 }}>
            Urgent cases are seen first and may be called before their turn. <span lang="ur" style={{ fontFamily: 'var(--font-ur)' }}>ہنگامی مریضوں کو ترجیح دی جاتی ہے۔</span>
          </p>
        </section>
      </div>

      <footer className="display-foot">
        <span>Screened today: <b>{board?.stats.screened ?? '—'}</b></span>
        <span>Average screening: <b>{board?.stats.avgMinutes != null ? `${board.stats.avgMinutes} min` : '—'}</b></span>
        <span>Teleconsults completed: <b>{board?.stats.teleconsults ?? '—'}</b></span>
        <span className="powered">POWERED BY <span>MOVO-X</span></span>
      </footer>
    </div>
  );
}
